import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  ContactsManagerErrorBannerState,
  ContactsManagerErrorBannerTypesEnum,
} from '../../../store/panels/app/banners/bannersState';
import {Banner, BannerTypesEnum} from '../../common/banners/banner';
import {BannerCloseAction} from '../../common/banners/bannerCloseAction';
import {BannerContent} from '../../common/banners/bannerContent';
import {BannerContentTitle} from '../../common/banners/bannerContentTitle';
import {BannerIcon} from '../../common/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../common/banners/bannersContext';

/*
 * Props.
 */

interface ContactsManagerErrorBannerProps {
  banner: ContactsManagerErrorBannerState;
  onClose: Handler;
}

/*
 * Styles.
 */

const StyledBannerContent = styled(BannerContent)`
  width: 183px; // gives us more room to fit text
`;

/*
 * Component.
 */

export const ContactsManagerErrorBanner: FC<ContactsManagerErrorBannerProps> = (props) => {
  const theme = useUserTheme();
  const {banner, onClose} = props;

  return (
    <Banner
      bannerType={BannerTypesEnum.MULTILINE}
      color={theme.palette.red.shade40}
      priority={BannerPrioritiesEnum.ERROR}
    >
      <BannerCloseAction onClick={onClose} />
      <BannerIcon iconName="warningFilledSmall" />
      <StyledBannerContent>
        <BannerContentTitle>{renderBannerText(banner.contactsManagerErrorType)}</BannerContentTitle>
        <Trans id="contacts-manager-error-banner-try-again">Please try again</Trans>
      </StyledBannerContent>
    </Banner>
  );
};

/*
 * Helpers.
 */

function renderBannerText(errorType: ContactsManagerErrorBannerTypesEnum) {
  switch (errorType) {
    case ContactsManagerErrorBannerTypesEnum.MERGE_FAILED:
      return <Trans id="contacts-manager-error-banner-merge-failed">Contacts merge failed</Trans>;
    case ContactsManagerErrorBannerTypesEnum.CONTACT_CREATE_FAILED:
      return <Trans id="contacts-manager-error-banner-contact-create-failed">Contact creation failed</Trans>;
    case ContactsManagerErrorBannerTypesEnum.SYNC_ACCOUNTS_REQUEST_FAILED:
      return (
        <Trans id="contacts-manager-error-banner-sync-accounts-failed-start">
          Account sync failed to start
        </Trans>
      );
    case ContactsManagerErrorBannerTypesEnum.SYNC_CARDS_REQUEST_FAILED:
      return (
        <Trans id="contacts-manager-error-banner-sync-contacts-failed-start">
          Contact sync failed to start
        </Trans>
      );
    default:
      return null;
  }
}
